import React, { useState, useEffect, useCallback } from 'react';
import { getSoundManager, SFX_KEYS } from '../game/systems/SoundManager';

// ============ TYPES ============

interface FishEntry {
  name: string;
  category: 'common' | 'uncommon' | 'rare' | 'legendary';
  baseValue: number;
  weight: number;
}

interface FishingSpotResearchData {
  spotId: string;
  spotType: string;
  typeName: string;
  description: string;
  catchChance: number;
  fishTable: FishEntry[];
}

interface FishingSpotResearchPanelProps {
  isOpen: boolean;
  onClose: () => void;
  spotId: string | null;
  spotType: string | null;
  spotPosition: { x: number; y: number } | null;
  playerPosition: { x: number; y: number } | null;
}

// ============ CONSTANTS ============

const RESEARCH_DURATION_MS = 2500; // 2.5 seconds for fishing spots (faster than islands)

// Fishing spot type info
const SPOT_TYPE_INFO: Record<string, { name: string; description: string; catchChance: number }> = {
  shallow_waters: {
    name: 'Shallow Waters',
    description: 'Calm, clear waters near the shore. Common fish are plentiful here.',
    catchChance: 45,
  },
  deep_sea: {
    name: 'Deep Sea',
    description: 'Dark, mysterious waters where larger fish lurk in the depths.',
    catchChance: 38,
  },
  coral_reef: {
    name: 'Coral Reef',
    description: 'A vibrant underwater ecosystem teeming with colorful exotic fish.',
    catchChance: 34,
  },
  fishing_hole: {
    name: '★ Fishing Hole',
    description: 'A legendary spot where the rarest catches have been reported!',
    catchChance: 30,
  },
};

// Fish loot tables by spot type
const FISH_TABLES: Record<string, FishEntry[]> = {
  shallow_waters: [
    { name: 'Sardine', category: 'common', baseValue: 10, weight: 40 },
    { name: 'Mackerel', category: 'common', baseValue: 25, weight: 30 },
    { name: 'Sea Bass', category: 'common', baseValue: 40, weight: 20 },
    { name: 'Tropical Fish', category: 'uncommon', baseValue: 80, weight: 8 },
    { name: 'Golden Koi', category: 'rare', baseValue: 400, weight: 2 },
  ],
  deep_sea: [
    { name: 'Mackerel', category: 'common', baseValue: 25, weight: 20 },
    { name: 'Squid', category: 'common', baseValue: 35, weight: 25 },
    { name: 'Bluefin Tuna', category: 'uncommon', baseValue: 100, weight: 25 },
    { name: 'Swordfish', category: 'uncommon', baseValue: 150, weight: 15 },
    { name: 'Octopus', category: 'uncommon', baseValue: 120, weight: 10 },
    { name: 'Giant Bluefin Tuna', category: 'rare', baseValue: 500, weight: 4 },
    { name: 'Ghost Fish', category: 'legendary', baseValue: 2000, weight: 1 },
  ],
  coral_reef: [
    { name: 'Tropical Fish', category: 'uncommon', baseValue: 80, weight: 35 },
    { name: 'Sea Bass', category: 'common', baseValue: 40, weight: 25 },
    { name: 'Octopus', category: 'uncommon', baseValue: 120, weight: 20 },
    { name: 'Rainbow Fish', category: 'rare', baseValue: 450, weight: 15 },
    { name: 'Golden Koi', category: 'rare', baseValue: 400, weight: 5 },
  ],
  fishing_hole: [
    { name: 'Bluefin Tuna', category: 'uncommon', baseValue: 100, weight: 20 },
    { name: 'Giant Bluefin Tuna', category: 'rare', baseValue: 500, weight: 25 },
    { name: 'Swordfish', category: 'uncommon', baseValue: 150, weight: 20 },
    { name: 'Golden Koi', category: 'rare', baseValue: 400, weight: 15 },
    { name: 'Juvenile Sea King', category: 'legendary', baseValue: 5000, weight: 5 },
    { name: 'Ghost Fish', category: 'legendary', baseValue: 2000, weight: 8 },
    { name: 'Ancient Coelacanth', category: 'legendary', baseValue: 4000, weight: 7 },
  ],
};

// Inject shimmer animation styles
const injectStyles = (): void => {
  const styleId = 'fishing-research-style';
  if (document.getElementById(styleId)) return;
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes fishingResearchProgress {
      0% { width: 0%; }
      100% { width: 100%; }
    }
    @keyframes fishingResearchPulse {
      0%, 100% { opacity: 0.6; }
      50% { opacity: 1; }
    }
    @keyframes fishingBubble {
      0% { transform: translateY(0) scale(1); opacity: 0.8; }
      100% { transform: translateY(-20px) scale(0.5); opacity: 0; }
    }
  `;
  document.head.appendChild(style);
};

// Category colors
const CATEGORY_COLORS: Record<string, string> = {
  common: '#9ca3af',
  uncommon: '#22c55e',
  rare: '#3b82f6',
  legendary: '#f59e0b',
};

// ============ COMPONENT ============

export const FishingSpotResearchPanel: React.FC<FishingSpotResearchPanelProps> = ({
  isOpen,
  onClose,
  spotId,
  spotType,
  spotPosition,
  playerPosition,
}) => {
  const [isResearching, setIsResearching] = useState<boolean>(false);
  const [researchProgress, setResearchProgress] = useState<number>(0);
  const [researchData, setResearchData] = useState<FishingSpotResearchData | null>(null);

  // Inject styles on mount
  useEffect(() => {
    injectStyles();
  }, []);

  // Reset state when panel opens/closes or spot changes
  useEffect(() => {
    if (isOpen && spotId) {
      setIsResearching(false);
      setResearchProgress(0);
      setResearchData(null);
    }
  }, [isOpen, spotId]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Generate research data from spot type
  const generateResearchData = useCallback((): void => {
    if (!spotId || !spotType) return;

    const typeInfo = SPOT_TYPE_INFO[spotType] || SPOT_TYPE_INFO.shallow_waters;
    const fishTable = FISH_TABLES[spotType] || FISH_TABLES.shallow_waters;

    setResearchData({
      spotId,
      spotType,
      typeName: typeInfo.name,
      description: typeInfo.description,
      catchChance: typeInfo.catchChance,
      fishTable,
    });
  }, [spotId, spotType]);

  // Start research process
  const startResearch = useCallback((): void => {
    if (isResearching || researchData) return;

    // Play research start SFX
    const soundManager = getSoundManager();
    soundManager?.play(SFX_KEYS.RESEARCH_START);

    setIsResearching(true);
    setResearchProgress(0);

    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(100, (elapsed / RESEARCH_DURATION_MS) * 100);
      setResearchProgress(progress);

      if (progress >= 100) {
        clearInterval(interval);
        setIsResearching(false);
        // Play research complete SFX
        soundManager?.play(SFX_KEYS.RESEARCH_COMPLETE);
        generateResearchData();
      }
    }, 50);
  }, [isResearching, researchData, generateResearchData]);

  // Cancel research if panel closes during research
  useEffect(() => {
    if (!isOpen && isResearching) {
      setIsResearching(false);
      setResearchProgress(0);
    }
  }, [isOpen, isResearching]);

  if (!isOpen || !spotId) return null;

  // Calculate total weight for percentage
  const totalWeight = researchData?.fishTable.reduce((sum, f) => sum + f.weight, 0) || 1;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#0d1117',
          border: '2px solid #30363d',
          padding: 0,
          minWidth: '300px',
          maxWidth: '380px',
          fontFamily: '"Sometype Mono", monospace',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: '1px solid #30363d',
            backgroundColor: '#161b22',
          }}
        >
          <span style={{ color: '#5dade2', fontSize: '14px', fontWeight: 'bold' }}>
            FISHING SPOT ANALYSIS
          </span>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: '1px solid #f85149',
              color: '#f85149',
              cursor: 'pointer',
              fontSize: '14px',
              padding: '4px 8px',
              fontFamily: '"Sometype Mono", monospace',
              fontWeight: 'bold',
            }}
          >
            x
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '16px' }}>
          {/* Not yet researched - two column layout */}
          {!researchData && !isResearching && (
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              {/* Left column - icon */}
              <div style={{
                width: '64px',
                height: '64px',
                flexShrink: 0,
                border: '2px dashed #5dade2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <span style={{ color: '#5dade2', fontSize: '28px', fontWeight: 'bold' }}>~</span>
              </div>
              {/* Right column - description and button */}
              <div style={{ flex: 1 }}>
                <p style={{ color: '#8b949e', fontSize: '12px', margin: '0 0 12px 0' }}>
                  Analyze this fishing spot to discover potential catches.
                </p>
                <button
                  onClick={startResearch}
                  style={{
                    backgroundColor: '#1d4ed8',
                    color: '#ffffff',
                    border: 'none',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontFamily: '"Sometype Mono", monospace',
                    fontWeight: 'bold',
                  }}
                >
                  ANALYZE WATERS
                </button>
              </div>
            </div>
          )}

          {/* Researching - two column layout */}
          {isResearching && (
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              {/* Left column - animated icon */}
              <div style={{
                width: '64px',
                height: '64px',
                flexShrink: 0,
                border: '2px solid #5dade2',
                animation: 'fishingResearchPulse 0.8s ease-in-out infinite',
                position: 'relative',
                overflow: 'hidden',
              }}>
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    backgroundColor: '#5dade2',
                    animation: 'fishingResearchProgress 1.2s ease-in-out infinite',
                  }}
                />
              </div>
              {/* Right column - progress */}
              <div style={{ flex: 1 }}>
                <p style={{ color: '#5dade2', fontSize: '12px', margin: '0 0 8px 0' }}>
                  Analyzing water conditions...
                </p>
                <div
                  style={{
                    backgroundColor: '#21262d',
                    height: '6px',
                    overflow: 'hidden',
                    marginBottom: '4px',
                  }}
                >
                  <div
                    style={{
                      backgroundColor: '#5dade2',
                      height: '100%',
                      width: `${researchProgress}%`,
                      transition: 'width 0.05s linear',
                    }}
                  />
                </div>
                <p style={{ color: '#8b949e', fontSize: '10px', margin: 0 }}>
                  {Math.round(researchProgress)}% complete
                </p>
              </div>
            </div>
          )}

          {/* Research complete - show data */}
          {researchData && (
            <div>
              {/* Spot type and name */}
              <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                <h3 style={{ color: '#5dade2', fontSize: '16px', margin: '0 0 4px 0' }}>
                  {researchData.typeName}
                </h3>
                <span style={{ color: '#8b949e', fontSize: '10px' }}>
                  Catch Rate: {researchData.catchChance}%
                </span>
              </div>

              {/* Description */}
              <div
                style={{
                  backgroundColor: '#161b22',
                  padding: '10px',
                  border: '1px solid #30363d',
                  marginBottom: '12px',
                }}
              >
                <p style={{ color: '#c9d1d9', fontSize: '11px', margin: 0, lineHeight: '1.5' }}>
                  {researchData.description}
                </p>
              </div>

              {/* Fish table */}
              <div
                style={{
                  backgroundColor: '#161b22',
                  padding: '10px',
                  border: '1px solid #30363d',
                }}
              >
                <div style={{ color: '#8b949e', fontSize: '9px', marginBottom: '8px' }}>
                  POTENTIAL CATCHES
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {researchData.fishTable.map((fish, i) => {
                    const chance = Math.round((fish.weight / totalWeight) * 100);
                    return (
                      <div
                        key={i}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '4px 6px',
                          backgroundColor: '#21262d',
                          border: `1px solid ${CATEGORY_COLORS[fish.category]}40`,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span
                            style={{
                              width: '8px',
                              height: '8px',
                              backgroundColor: CATEGORY_COLORS[fish.category],
                              display: 'inline-block',
                            }}
                          />
                          <span style={{ color: '#c9d1d9', fontSize: '11px' }}>
                            {fish.name}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ color: '#ffd700', fontSize: '10px' }} title="Price varies by market conditions">
                            ₱{Math.round(fish.baseValue * 0.7)}-{Math.round(fish.baseValue * 1.3)}
                          </span>
                          <span style={{ color: '#8b949e', fontSize: '9px', width: '30px', textAlign: 'right' }}>
                            {chance}%
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Legend */}
              <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
                {Object.entries(CATEGORY_COLORS).map(([cat, color]) => (
                  <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ width: '6px', height: '6px', backgroundColor: color, display: 'inline-block' }} />
                    <span style={{ color: '#8b949e', fontSize: '8px', textTransform: 'capitalize' }}>{cat}</span>
                  </div>
                ))}
              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  );
};
